import { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { isProd } from 'src/config';
import SearchFilterBar, {
  resolveQuickSearch,
  type QuickFiltersType,
} from 'src/components/SearchFilterBar';
import DataTable from 'src/components/DataTable';
import PageLayout from 'src/components/PageLayout';
import { isOlderThanThreeDays } from 'src/utils/datetime';

// SVGs
import { ReactComponent as PlayIcon } from 'src/images/Play.svg';
import { ReactComponent as PencilIcon } from 'src/images/Pencil.svg';
import { ReactComponent as DisablePlay } from 'src/images/DisablePlay.svg';
import { ReactComponent as PersonEditIcon } from 'src/images/PersonEdit.svg';
import { ReactComponent as TransitionPushIcon } from 'src/images/TransitionPush.svg';
import { ReactComponent as AssignmentTurnedInIcon } from 'src/images/AssignmentTurnedIn.svg';

import EvaluationFormModal from './components/EvaluationFormModal';
import PreRegisterFormModal from './components/PreRegisterFormModal';
import { useUpdatePrescreening } from './api/mutations/useUpdatePrescreening';

import messages from './messages';
import { useGetUserAccounts } from './api';
import { useCreatePrescreening } from './api';
import { useCreateUserAccount, useUpdateUserAccount } from './api';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useUpdateGrantStudentAccess } from './api/mutations/useUpdateGrantStudentAccess';
import { Styled } from './index.styled';
import DeleteUserAccountButton from './components/DeleteUserAccountButton';

const navTabs: Array<{ value: UCM.CandidateStatusType; label: string }> = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'pre_registered',
    label: 'Pre-registered',
  },
  {
    value: 'app_registered',
    label: 'App-registered',
  },
  {
    value: 'prescreening_missing',
    label: 'Interview missing',
  },
  {
    value: 'awaiting_evaluation',
    label: 'To evaluate',
  },
  {
    value: 'queued',
    label: 'Queued',
  },
  // {
  //   value: 'access_paused',
  //   label: 'Access paused',
  // },
];

export default function GatekeeperPage() {
  const { formatMessage } = useIntl();

  const { mutate: createUserAccount } = useCreateUserAccount();
  const { mutate: updateUserAccount } = useUpdateUserAccount();
  const { mutate: createPrescreening } = useCreatePrescreening();
  const { mutate: updatePrescreening } = useUpdatePrescreening();
  const { mutate: updateStudentAccess } = useUpdateGrantStudentAccess();

  const [activeTab, setActiveTab] = useState<UCM.CandidateStatusType>(
    navTabs[0].value,
  );
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: userAccounts, isFetching: isFetchingUserAccounts } =
    useGetUserAccounts(activeTab, params, resolveQuickSearch(filters));

  const quickFilters: Array<QuickFiltersType> = useRef([
    {
      autoFocus: true,
      id: 'quick_search',
      type: 'text' as QuickFiltersType['type'],
      placeholder: formatMessage(messages.searchInputPlaceholder),
    },
  ]).current;

  const firstFixedColumns = useMemo(() => {
    return [
      {
        id: 'email',
        label: formatMessage(messages.emailColumnLabel),
      },
      {
        id: 'phoneNumber',
        label: formatMessage(messages.phoneColumnLabel),
        render: (phoneNumber: string | null) => (
          <Typography variant="inherit">{phoneNumber || '-'}</Typography>
        ),
      },
      {
        id: 'firstName',
        label: formatMessage(messages.firstNameColumnLabel),
        render: (firstName: string) => (
          <Typography variant="inherit">{firstName}</Typography>
        ),
      },
      {
        id: 'lastName',
        label: formatMessage(messages.lastNameColumnLabel),
        render: (lastName: string) => (
          <Typography variant="inherit">{lastName}</Typography>
        ),
      },
    ];
  }, []);

  const statusColumn = useMemo(() => {
    return {
      id: 'status',
      align: 'center',
      label: formatMessage(messages.statusColumnLabel),
      render: (_: Object, { prescreening }: UCM.UserAccountType) => (
        <Typography variant="inherit">{prescreening?.status || '-'}</Typography>
      ),
    };
  }, []);

  const basicProfileCompletedColumn = useMemo(() => {
    return {
      id: 'basicProfileCompletedAt',
      align: 'center',
      label: formatMessage(messages.baseProfileSinceColumnLabel),
      render: (completedAt: UCM.UserAccountType['basicProfileCompletedAt']) => {
        if (!completedAt) return <Typography variant="inherit">-</Typography>;
        const isOlder = isOlderThanThreeDays(completedAt);
        return (
          <Typography
            variant="inherit"
            style={{ color: isOlder ? 'orangeRed' : 'inherit' }}
          >
            {completedAt}
          </Typography>
        );
      },
    };
  }, []);

  const prescreeningEvaluationColumn = useMemo(() => {
    return {
      id: 'prescreening',
      align: 'center',
      label: formatMessage(messages.evaluatedSinceColumnLabel),
      render: (prescreening: UCM.UserAccountType['prescreening']) => {
        if (!prescreening?.createdAt)
          return <Typography variant="inherit">-</Typography>;
        const isOlder = isOlderThanThreeDays(prescreening.createdAt);
        return (
          <Typography variant="inherit" color={isOlder ? 'error' : 'inherit'}>
            {prescreening.createdAt}
          </Typography>
        );
      },
    };
  }, []);

  const interviewCompletedColumn = useMemo(() => {
    return {
      id: 'interviewCompletedAt',
      align: 'center',
      label: formatMessage(messages.interviewCompletedColumnLabel),
      render: (
        interviewCompletedAt: UCM.UserAccountType['interviewCompletedAt'],
      ) => {
        if (!interviewCompletedAt)
          return <Typography variant="inherit">-</Typography>;
        const isOlder = isOlderThanThreeDays(interviewCompletedAt);
        return (
          <Typography variant="inherit" color={isOlder ? 'error' : 'inherit'}>
            {interviewCompletedAt}
          </Typography>
        );
      },
    };
  }, []);

  const userPotentialColumn = useMemo(() => {
    return {
      id: 'userPotential',
      align: 'center',
      label: formatMessage(messages.userProfileColumnLabel),
      render: (user_potential: number) => (
        <Typography variant="inherit">{user_potential || '-'}</Typography>
      ),
    };
  }, []);

  const columns: Record<
    UCM.CandidateStatusType,
    Array<{ id: keyof UCM.UserAccountType | string; label: string }>
  > = useMemo(
    () => ({
      all: [
        ...firstFixedColumns,
        {
          id: 'stage',
          align: 'center',
          label: formatMessage(messages.stageColumnLabel),
          render: (stage: string | null) => (
            <Typography variant="inherit">{stage}</Typography>
          ),
        },
        {
          id: 'stageTime',
          align: 'center',
          label: formatMessage(messages.stageTimeColumnLabel),
          render: (stageTime: string | null) => {
            if (!stageTime) return <Typography variant="inherit">-</Typography>;
            const isOlder = isOlderThanThreeDays(stageTime);
            return (
              <Typography
                variant="inherit"
                color={isOlder ? 'error' : 'inherit'}
              >
                {stageTime}
              </Typography>
            );
          },
        },
      ],
      pre_registered: [
        ...firstFixedColumns,
        {
          id: 'createdAt',
          align: 'center',
          label: formatMessage(messages.leadSinceColumnLabel),
          render: (createdAt: string) => {
            const isOlder = isOlderThanThreeDays(createdAt);
            return (
              <Typography
                variant="inherit"
                color={isOlder ? 'error' : 'inherit'}
              >
                {createdAt}
              </Typography>
            );
          },
        },
        // TODO - BE not supported for this yet
        // {
        //   id: 'campaign',
        //   label: formatMessage(messages.campaignColumnLabel),
        //   render: () => '-',
        // },
        statusColumn,
        {
          id: 'actions',
          align: 'center',
          label: '',
          width: 0,
          whiteSpace: 'nowrap',
          render: (_: Object, item: UCM.UserAccountType) => {
            return (
              <Grid display="flex" alignItems="center">
                <PreRegisterFormModal
                  isEdit
                  userUuid={item.userUuid}
                  onUpdate={updateUserAccount}
                  triggerButton={(handleOpen) => (
                    <Tooltip title={formatMessage(messages.editPreregHint)}>
                      <IconButton onClick={handleOpen}>
                        <PersonEditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                />
                <EvaluationFormModal
                  userEmail={item.email}
                  userUuid={item.userUuid}
                  onUpdate={updatePrescreening}
                  onCreate={createPrescreening}
                  isEdit={item.prescreening != null}
                  uuid={item.prescreening?.uuid ?? ''}
                  triggerButton={(handleOpen) => {
                    const isEditMode = item.prescreening != null;
                    const iconHint = isEditMode
                      ? messages.editPrescreenHint
                      : messages.createPrescreenHint;

                    return (
                      <Tooltip title={formatMessage(iconHint)}>
                        <IconButton onClick={handleOpen}>
                          {isEditMode ? (
                            <PencilIcon fontSize="small" />
                          ) : (
                            <AssignmentTurnedInIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    );
                  }}
                />
                <DeleteUserAccountButton userUuid={item.userUuid} />
              </Grid>
            );
          },
        },
      ],
      app_registered: [
        ...firstFixedColumns,
        {
          id: 'createdAt',
          align: 'center',
          label: formatMessage(messages.accountSinceColumnLabel),
          render: (createdAt: string) => {
            const isOlder = isOlderThanThreeDays(createdAt);
            return (
              <Typography
                variant="inherit"
                color={isOlder ? 'error' : 'inherit'}
              >
                {createdAt}
              </Typography>
            );
          },
        },
        prescreeningEvaluationColumn,
        statusColumn,
        {
          id: 'actions',
          label: '',
          align: 'center',
          width: 0,
          whiteSpace: 'nowrap',
          render: (_: Object, item: UCM.UserAccountType) => {
            return (
              <Grid display="flex" alignItems="center">
                <EvaluationFormModal
                  userEmail={item.email}
                  userUuid={item.userUuid}
                  onUpdate={updatePrescreening}
                  onCreate={createPrescreening}
                  isEdit={item.prescreening != null}
                  uuid={item.prescreening?.uuid ?? ''}
                  triggerButton={(handleOpen) => {
                    const isEditMode = item.prescreening != null;
                    const iconHint = isEditMode
                      ? messages.editPrescreenHint
                      : messages.createPrescreenHint;

                    return (
                      <Tooltip title={formatMessage(iconHint)}>
                        <IconButton onClick={handleOpen}>
                          {isEditMode ? (
                            <PencilIcon fontSize="small" />
                          ) : (
                            <AssignmentTurnedInIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    );
                  }}
                />
                {forceAccessComponent(item.userUuid)}
                <DeleteUserAccountButton userUuid={item.userUuid} />
              </Grid>
            );
          },
        },
      ],
      prescreening_missing: [
        ...firstFixedColumns,
        statusColumn,
        basicProfileCompletedColumn,
        userPotentialColumn,
        {
          id: 'actions',
          align: 'center',
          width: 0,
          whiteSpace: 'nowrap',
          label: formatMessage(messages.evaluateColumnLabel),
          render: (_: Object, item: UCM.UserAccountType) => {
            return (
              <Grid display="flex" alignItems="center">
                <EvaluationFormModal
                  userEmail={item.email}
                  userUuid={item.userUuid}
                  onUpdate={updatePrescreening}
                  onCreate={createPrescreening}
                  isEdit={item.prescreening != null}
                  uuid={item.prescreening?.uuid ?? ''}
                  triggerButton={(handleOpen) => {
                    const isEditMode = item.prescreening != null;
                    const iconHint = isEditMode
                      ? messages.editPrescreenHint
                      : messages.createPrescreenHint;

                    return (
                      <Tooltip title={formatMessage(iconHint)}>
                        <IconButton onClick={handleOpen}>
                          {isEditMode ? (
                            <PencilIcon fontSize="small" />
                          ) : (
                            <AssignmentTurnedInIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    );
                  }}
                />
                {forceAccessComponent(item.userUuid)}
                <DeleteUserAccountButton userUuid={item.userUuid} />
              </Grid>
            );
          },
        },
      ],
      awaiting_evaluation: [
        ...firstFixedColumns,
        interviewCompletedColumn,
        basicProfileCompletedColumn,
        userPotentialColumn,
        {
          id: 'video',
          align: 'center',
          label: formatMessage(messages.videoColumnLabel),
          render: (_: Object, { prescreening }: UCM.UserAccountType) => {
            if (!prescreening?.interviewManagerUrl || !prescreening?.showUrl)
              return (
                <Tooltip title={formatMessage(messages.videoUrlHint)}>
                  <DisablePlay />
                </Tooltip>
              );

            return (
              <Tooltip title={formatMessage(messages.videoUrlHint)}>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={prescreening.interviewManagerUrl}
                >
                  <PlayIcon />
                </Link>
              </Tooltip>
            );
          },
        },
        {
          id: 'actions',
          align: 'center',
          width: 0,
          whiteSpace: 'nowrap',
          label: formatMessage(messages.evaluateColumnLabel),
          render: (_: Object, item: UCM.UserAccountType) => {
            return (
              <Grid display="flex" alignItems="center">
                <EvaluationFormModal
                  userEmail={item.email}
                  userUuid={item.userUuid}
                  onUpdate={updatePrescreening}
                  onCreate={createPrescreening}
                  isEdit={item.prescreening != null}
                  uuid={item.prescreening?.uuid ?? ''}
                  triggerButton={(handleOpen) => {
                    const isEditMode = item.prescreening != null;
                    const iconHint = isEditMode
                      ? messages.editPrescreenHint
                      : messages.createPrescreenHint;

                    return (
                      <Tooltip title={formatMessage(iconHint)}>
                        <IconButton onClick={handleOpen}>
                          {isEditMode ? (
                            <PencilIcon fontSize="small" />
                          ) : (
                            <AssignmentTurnedInIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    );
                  }}
                />
                {forceAccessComponent(item.userUuid)}
                <DeleteUserAccountButton userUuid={item.userUuid} />
              </Grid>
            );
          },
        },
      ],
      queued: [
        ...firstFixedColumns,
        prescreeningEvaluationColumn,
        {
          id: 'evaluatedBy',
          align: 'center',
          label: formatMessage(messages.evaluatedByColumnLabel),
          render: (_: Object, { prescreening }: UCM.UserAccountType) => (
            <Typography variant="inherit">
              {prescreening?.verifiedBy || '-'}
            </Typography>
          ),
        },
        {
          id: 'accessStatus',
          align: 'center',
          label: formatMessage(messages.reasonColumnLabel),
          render: (status: string | null, item: UCM.UserAccountType) => (
            <Grid container alignItems="center" justifyContent="center">
              <Tooltip
                arrow
                title={
                  <Stack spacing={0.5} py={0.5}>
                    {item?.birthDate && (
                      <Typography variant="inherit">
                        {formatMessage(messages.birthDateTooltipLabel, {
                          date: item?.birthDate,
                        })}
                      </Typography>
                    )}
                    {item?.adultOn && (
                      <Typography variant="inherit">
                        {formatMessage(messages.adultOnTooltipLabel, {
                          date: item?.adultOn,
                        })}
                      </Typography>
                    )}
                    {item?.occupationCode && (
                      <Typography variant="inherit">
                        {formatMessage(messages.occupationTypeTooltipLabel, {
                          type: item?.occupationCode,
                        })}
                      </Typography>
                    )}
                    {item?.workPermitted && (
                      <Typography variant="inherit">
                        {formatMessage(messages.workPermitTooltipLabel, {
                          permit: item?.workPermitted
                            ? formatMessage(messages.workPermitYesTooltipLabel)
                            : formatMessage(messages.workPermitNoTooltipLabel),
                        })}
                      </Typography>
                    )}
                  </Stack>
                }
              >
                <Grid container spacing={0.5} alignItems="center">
                  <Typography variant="inherit">{status || '-'}</Typography>
                  <Styled.SmallInfoOutlinedIcon />
                </Grid>
              </Tooltip>
            </Grid>
          ),
        },
        {
          id: 'actions',
          label: '',
          width: 0,
          whiteSpace: 'nowrap',
          render: (_: Object, item: UCM.UserAccountType) => {
            return (
              <Grid display="flex" alignItems="center">
                <EvaluationFormModal
                  userEmail={item.email}
                  userUuid={item.userUuid}
                  onUpdate={updatePrescreening}
                  onCreate={createPrescreening}
                  isEdit={item.prescreening != null}
                  uuid={item.prescreening?.uuid ?? ''}
                  triggerButton={(handleOpen) => {
                    const isEditMode = item.prescreening != null;
                    const iconHint = isEditMode
                      ? messages.editPrescreenHint
                      : messages.createPrescreenHint;

                    return (
                      <Tooltip title={formatMessage(iconHint)}>
                        <IconButton onClick={handleOpen}>
                          {isEditMode ? (
                            <PencilIcon fontSize="small" />
                          ) : (
                            <AssignmentTurnedInIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    );
                  }}
                />
                {forceAccessComponent(item.userUuid)}
                <DeleteUserAccountButton userUuid={item.userUuid} />
              </Grid>
            );
          },
        },
      ],
      // access_paused: [
      //   {
      //     id: 'email',
      //     label: formatMessage(messages.emailColumnLabel),
      //   },
      // {
      //   id: 'firstName',
      //   label: formatMessage(messages.firstNameColumnLabel),
      //   render: (firstName: string) => {
      //     return <Typography variant="inherit">{firstName}</Typography>;
      //   },
      // },
      // {
      //   id: 'lastName',
      //   label: formatMessage(messages.lastNameColumnLabel),
      //   render: (lastName: string) => {
      //     return <Typography variant="inherit">{lastName}</Typography>;
      //   },
      // },
      //   {
      //     id: 'phoneNumber',
      //     label: formatMessage(messages.phoneColumnLabel),
      //     render: (phoneNumber: string | null) => {
      //       if (phoneNumber == null) return '-';
      //       return <Typography variant="inherit">{phoneNumber}</Typography>;
      //     },
      //   },
      //   {
      //     id: 'accessPausedAt',
      //     label: formatMessage(messages.accessPausedColumnLabel),
      //     render: (pausedAt: string | null) => {
      //       if (pausedAt == null) return '-';
      //       return <Typography variant="inherit">{pausedAt}</Typography>;
      //     },
      //   },
      //   {
      //     id: 'accessPausedReasons',
      //     label: formatMessage(messages.accessReasonsLabel),
      //     render: (reasons: UCM.UserAccountType['accessPausedReasons']) => {
      //       if (reasons == null) return '-';

      //       return (
      //         <ul>
      //           {reasons.map(({ code, title }) => (
      //             <li key={code}>{title}</li>
      //           ))}
      //         </ul>
      //       );
      //     },
      //   },
      //   {
      //     id: 'actions',
      //     label: '',
      //     width: 0,
      //     whiteSpace: 'nowrap',
      //     render: (_: Object, item: UCM.UserAccountType) => {
      //       return (
      //         <Grid container>
      //           <Grid size={{ lg: 6, md: 12, sm: 12 }}>
      //             <EvaluationFormModal
      //               userEmail={item.email}
      //               userUuid={item.userUuid}
      //               onUpdate={updatePrescreening}
      //               onCreate={createPrescreening}
      //               isEdit={item.prescreening != null}
      //               uuid={item.prescreening?.uuid ?? ''}
      //               triggerButton={(handleOpen) => {
      //                 const isEditMode = item.prescreening != null;
      //                 const iconHint = isEditMode
      //                   ? messages.editPrescreenHint
      //                   : messages.createPrescreenHint;

      //                 return (
      //                   <Tooltip title={formatMessage(iconHint)}>
      //                     <IconButton onClick={handleOpen}>
      //                       {isEditMode ? (
      //                         <PencilIcon />
      //                       ) : (
      //                         <AssignmentTurnedInIcon />
      //                       )}
      //                     </IconButton>
      //                   </Tooltip>
      //                 );
      //               }}
      //             />
      //           </Grid>

      //           <Grid size={{ lg: 6, md: 12, sm: 12 }}>
      //             {forceAccessComponent(item.userUuid)}
      //           </Grid>
      //         </Grid>
      //       );
      //     },
      //   },
      // ],
    }),
    [activeTab],
  );

  const forceAccessComponent = useCallback((candidateUuid: string) => {
    if (isProd) {
      return null;
    }

    return (
      <ConfirmationDialog
        maxWidth="sm"
        onConfirm={() => updateStudentAccess(candidateUuid)}
        message={formatMessage(messages.accessGrantedConfirmMessage)}
      >
        {(handleOpen) => (
          <Tooltip title={formatMessage(messages.forceAccessHint)}>
            <IconButton onClick={handleOpen}>
              <TransitionPushIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </ConfirmationDialog>
    );
  }, []);

  // eslint-disable-next-line
  const handleSearch = useCallback((filters: Record<string, any>) => {
    setParams({ page: 1 });
    setFilters(filters);
  }, []);

  const handlePagination = useCallback((event: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const handleActiveTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setParams({ page: 1 });
  }, []);

  return (
    <PageLayout
      iconName="gatekeeper"
      title={formatMessage(messages.title)}
      tabs={navTabs}
      activeTab={activeTab}
      onActiveTabChange={handleActiveTabChange}
    >
      <Grid container spacing={2} width="100%" mt={3}>
        <Grid size={3}>
          <SearchFilterBar
            fields={quickFilters}
            onSearch={handleSearch}
            variant="noBorder"
          />
        </Grid>

        <Grid
          size={9}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid size={6}>
              {activeTab === 'pre_registered' ? (
                <PreRegisterFormModal
                  isEdit={false}
                  onCreate={createUserAccount}
                />
              ) : null}
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <DataTable
            rows={userAccounts?.data}
            columns={columns[activeTab]}
            pagination={userAccounts?.meta}
            onPaginate={handlePagination}
            loading={isFetchingUserAccounts}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
}
